'use client'

import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import merge from 'lodash-es/merge'
import dynamic from 'next/dynamic'
import { ErrorModalArgs, ModalState, PartialModalState } from '@/types/ModalProvider.types'
import { ErrorType } from '@/types/errors.types'
import { defaultState } from '@/config/modal.config'
import ModalContext from './ModalContext'
import { clearModalCookieAction, getModalCookieAction } from '@/app/_actions/modal-cookie.actions'
// Dynamic imports. These are not included in the initial bundle
// https://nextjs.org/docs/pages/building-your-application/optimizing/lazy-loading#nextdynamic
const ErrorModal = dynamic(() => (
  import('@/app/_components/ErrorModal/ErrorModal')
), { ssr: false })

type Props = {
  children: React.ReactNode
}

/**
 * NOTE
 *
 * - show modal
 * - modal callbacks
 *  - confirm
 *  - cancel
 * - modal style
 * - modal features
 *  - show cancel
 *  - closable
 * - set title
 * - set content
 */

const ModalProvider: FunctionComponent<Props> = ({ children }: Props) => {
  const [modalState, setModalState] = useState<ModalState>(defaultState)

  const updateModalState = useCallback((nextState: PartialModalState): void => {
    setModalState(() => {
      const merged = merge(defaultState, nextState) as ModalState
      return { ...merged }
    })
  }, [])

  /**
   * Set the error type
   * @param type - type of error
   */
  const setErrorType = useCallback((type: ErrorType | null): void => {
    setModalState((prev) => ({ ...prev, errorType: type }))
  }, [])

  /**
   * Clean and set error message
   * @param message - optional error message
   */
  const setErrorMessage = useCallback((message: string): void => {
    setModalState((prev) => ({
      ...prev,
      errorMessage: message,
    }))
  }, [])

  const setErrorModal = useCallback(({ type, message, error: err }: ErrorModalArgs): void => {
    setModalState((prev) => ({
      ...defaultState,
      errorMessage: message ? message : prev.errorMessage,
      errorType: type,
      error: err ?? prev.error,
      open: {
        ...defaultState.open,
        error: !!type,
      },
    }))
  }, [])

  const setError = useCallback((err: Error): void => {
    setModalState((prev) => ({ ...prev, error: err }))
  }, [])

  useEffect(() => {
    let mounted = true

    const handleKeyDown = (e: KeyboardEvent): void => {
      // close all modals with escape
      if (e.key === 'Escape') {
        updateModalState({ open: { ...defaultState.open } })
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    // handle incoming cookie
    getModalCookieAction().then((result) => {
      if (!mounted || !result) return
      setErrorModal({ type: result.type, message: result.message })
      clearModalCookieAction()
    })

    return () => {
      mounted = false
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [setErrorModal, updateModalState])

  const value = useMemo(() => ({
    setErrorType,
    setErrorMessage,
    setErrorModal,
    setError,
    modalState,
    setModalState,
    updateModalState,
  }), [
    setErrorType,
    setErrorMessage,
    setErrorModal,
    setError,
    modalState,
    setModalState,
    updateModalState,
  ])

  return (
    <ModalContext.Provider value={value}>
      {modalState.open.error && modalState.errorType && (
        <ErrorModal
          aria-modal="true"
          aria-hidden={false}
          errorType={modalState.errorType}
          errorMsg={modalState.errorMessage}
          error={modalState.error || undefined}
        />
      )}
      {children}
    </ModalContext.Provider>
  )
}

export default ModalProvider
